<template>
    <div class="transport-wrapper mb-6">
        <div class="transport-component text-none pa-0 pa-md-6 pb-0 mb-0 mb-md-0" v-if="transport">
            <div class="number-wrapper text-left d-flex flex-column">
                <span class="d-flex align-center mb-2 mb-md-0">
                    <img :src="truck_icon" class="mr-2" />{{ $t('transport.number_label') }} #{{ transport.reservation_ids.join(', #') }}
                </span>
            </div>
            <div class="data-row d-flex flex-md-row flex-column pt-6 pr-0 pr-md-4 pl-0 pl-md-8">
                <div class="package-name-wrapper mb-6 mb-md-0">
                    <div class="label mb-2 text-left">
                        {{ $t('transport.type_label') }}
                    </div>
                    <div class="package-name-data text-left">
                        {{ getName() }}
                    </div>
                    <div class="text-left sub-price mt-1">
                        <span class="mt-2">
                            {{ $t(transport.material_localization_name) }}<img :src="shevronIcon" height="8" class="mx-2"/>{{ $t(transport.treatment_localization_name) }}
                        </span>
                    </div>
                </div>

                <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>

                <div class="quantity-wrapper mr-md-16 mr-0 mb-6 mb-md-0 w-50">
                    <div class="label mb-2 text-left text-md-right">
                        {{ $t('transport.quantity_label') }}
                    </div>
                    <div class="quantity-data text-md-right text-left">
                        1
                    </div>
                </div>

                <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>
                
                <div class="price-wrapper mr-md-16 mr-0 mb-6 mb-md-0 w-50">
                    <div class="label mb-2 text-md-right text-left">
                        {{ $t('transport.summary_label') }}
                    </div>
                    <div class="price-data text-md-right text-left">
                        <span :class="['price']">
                            {{ getValue() }}
                        </span>
                    </div>
                </div>

                <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>
                
                <div class="status-wrapper ml-md-4 ml-0">
                    <div class="label mb-2 text-left">
                        {{ $t('transport.status_label') }}
                    </div>
                    <div class="price-data text-left d-flex flex-row flex-md-column align-start">
                        <div :class="['status px-3 py-1 mb-md-2 mb-8', transport.status?.status]">
                            {{ transport.status ? this.$t(`status.${transport.status.status}_transport`,) : null }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="transport.status && ['PAID','PLANNED','SEND','FINISHED'].includes(transport.status.status)">
            <div v-if="!$vuetify.display.mobile" class="transport-steps-wraper pa-6 ml-12 mr-8 mb-6">
                <v-row>
                    <v-col cols="12">
                        <div class="d-flex align-center flex-row justify-space-between">
                            <div class="transport-step d-flex align-center">
                                <div :class="['step-no', getPreparationTransportStepClass(transport.status)]">
                                    1
                                </div>
                                <div class="step-desc d-flex flex-column align-start text-left ml-3">
                                    <div :class="['label']">
                                        {{ $t('transport.preparation_label') }}
                                    </div>
                                    <div class="step-date mt-1">
                                        {{ getDate(transport.status.paid_time) }}
                                    </div>
                                </div>
                            </div>

                            <div class="pause mx-2"></div>

                            <div class="transport-step d-flex align-center">
                                <div :class="['step-no', getPlannedShipmentTransportStepClass(transport.status)]">
                                    2
                                </div>
                                <div class="step-desc d-flex flex-column align-start text-left ml-3">
                                    <div :class="['label']">
                                        {{ $t('transport.planned_shipment_label') }}
                                    </div>
                                    <div class="step-date mt-1">
                                        {{ getDate(transport.status.planned_send_time) }}
                                    </div>
                                </div>
                            </div>

                            <div class="pause mx-2"></div>

                            <div class="transport-step d-flex align-center">
                                <div :class="['step-no', getDateSentTransportStepClass(transport.status)]">
                                    3
                                </div>
                                <div class="step-desc d-flex flex-column align-start text-left ml-3">
                                    <div :class="['label']">
                                        {{ $t('transport.date_sent_label') }}
                                    </div>
                                    <div class="step-date mt-1">
                                        {{ getDate(transport.status.send_time) }}
                                    </div>
                                </div>
                            </div>

                            <div class="pause mx-2"></div>

                            <div class="transport-step d-flex align-center">
                                <div :class="['step-no', getDeliveryToClinicTransportStepClass(transport.status)]">
                                    4
                                </div>
                                <div class="step-desc d-flex flex-column align-start text-left ml-3">
                                    <div :class="['label']">
                                        {{ $t('transport.delivery_to_clinic_label') }}
                                    </div>
                                    <div class="step-date mt-1">
                                        {{ getDate(transport.status.delivery_time) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
            <div v-else class="transport-steps-wraper pa-4 mx-0 mb-6">
                <swiper
                    :initial-slide="getCurrentTransportStep(transport.status)"              
                    :slides-per-view="1.5"
                    :space-between="50"
                    :slides-offset-after="200"
                    :slide-active-class="'slide-active'"
                    :resistance="true"
                    :resistance-ratio="0"
                >
                    <swiper-slide class="transport-step d-flex align-center">
                        <div :class="['step-no', getPreparationTransportStepClass(transport.status)]">1</div>
                        <div class="step-desc d-flex flex-column align-start text-left ml-3">
                            <div :class="['label']">
                                {{ $t('transport.preparation_label') }}
                            </div>
                            <div class="step-date mt-1">
                                {{ getDate(transport.status.create_time) }}
                            </div>
                        </div>
                    </swiper-slide>

                    <swiper-slide class="transport-step d-flex align-center">
                        <div :class="['step-no', getPlannedShipmentTransportStepClass(transport.status)]">2</div>
                        <div class="step-desc d-flex flex-column align-start text-left ml-3">
                            <div :class="['label']">
                                {{ $t('transport.planned_shipment_label') }}
                            </div>
                            <div class="step-date mt-1">
                                {{ getDate(transport.status.planned_send_time) }}
                            </div>
                        </div>
                    </swiper-slide>

                    <swiper-slide class="transport-step d-flex align-center">
                        <div :class="['step-no', getDateSentTransportStepClass(transport.status)]">3</div>
                        <div class="step-desc d-flex flex-column align-start text-left ml-3">
                            <div :class="['label']">
                                {{ $t('transport.date_sent_label') }}
                            </div>
                            <div class="step-date mt-1">
                                {{ getDate(transport.status.send_time) }}
                            </div>
                        </div>
                    </swiper-slide>

                    <swiper-slide class="transport-step d-flex align-center">
                        <div :class="['step-no', getDeliveryToClinicTransportStepClass(transport.status)]">4</div>
                        <div class="step-desc d-flex flex-column align-start text-left ml-3">
                            <div :class="['label']">
                                {{ $t('transport.delivery_to_clinic_label') }}
                            </div>
                            <div class="step-date mt-1">
                                {{ getDate(transport.status.delivery_time) }}
                            </div>
                        </div>
                    </swiper-slide>

                </swiper>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from "vue";
import { Swiper, SwiperSlide } from 'swiper/vue';

// Import Swiper styles
import 'swiper/css';

export default {
    name: "transport-component",
    data: () => {
        return {
            fetchingPaymentLink: false,
            showConfirmDialog: false,
        }
    },
    components: {
      Swiper,
      SwiperSlide
    },
    props: {
        transport: {
            type: Object
        }
    },
    emits: ['refresh'],
    setup() {
        const truck_icon = computed({
            get: () => { return require('../assets/icons/truck.svg'); }
        })
        const calendar_icon = computed({
            get: () => { return require('../assets/icons/calendar.svg'); }
        })
        const paidIcon = computed({
        get: () => { return require('@/assets/icons/paid.svg');}
        })
        const shevronIcon = computed({
            get: () => { return require('../assets/icons/chevron-right.svg'); }
        })

        return {
            truck_icon,
            calendar_icon,
            paidIcon,
            shevronIcon
        }
    },
    computed: {
        isInPaymentReservations() {
            return this.paymentReservations.includes(this.reservation.reservation_id)
        }
    },
    methods: {
        getName() {
            return this.transport.price_name;
        },
        getValue() {
            return this.$n(
                this.transport.price,
                'currency',
                'pl'
            );
        },
        getDate(datetime) {
            return datetime ? this.$d(
                datetime,
                'short',
                'pl'
            ) : '-';
        },
        getPreparationTransportStepClass(transportStatus) {
            return transportStatus?.status == 'PAID' ? 'current' : 'passed'
        },
        getPlannedShipmentTransportStepClass(transportStatus) {
            switch(transportStatus?.status) {
                case "PLANNED": return transportStatus?.planned_send_time ? 'passed' : 'current';
                case "SEND": return 'passed';
                case "FINISHED": return 'passed';
                default: return '';
            }
        },
        getDateSentTransportStepClass(transportStatus) {
            switch(transportStatus?.status) {
                case "PLANNED": return transportStatus?.planned_send_time ? 'current' : '';
                case "SEND": return transportStatus?.send_time ? 'passed' : 'current';
                case "FINISHED": return 'passed';
                default: return '';
            }
        },
        getDeliveryToClinicTransportStepClass(transportStatus) {
            switch(transportStatus?.status) {
                case "SEND": return transportStatus?.send_time ? 'current' : '';
                case "FINISHED": return 'passed';
                default: return '';
            }
        },
        getCurrentTransportStep(transportStatus) {
            if (this.getPreparationTransportStepClass(transportStatus) == 'current') {
                return 0;
            }
            if (this.getPlannedShipmentTransportStepClass(transportStatus) == 'current') {
                return 1;
            }
            if (this.getDateSentTransportStepClass(transportStatus) == 'current') {
                return 2;
            }
            if (['current','passed'].includes(this.getDeliveryToClinicTransportStepClass(transportStatus))) {
                return 3;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../node_modules/design-system-ui-components/dist/all.min.css";
.transport-wrapper {
    border-bottom: 1px solid $nsb-color-3;

    .deposit-info {
        position: relative;
        width: auto;
        width: calc(100% - 30px);
        padding-left: 50px;
    }
    .deposit-info-mobile {
        position: relative;
        width: auto;
        width: 100%;
    }
    :deep(.notification--icon) {
        min-width: 24px !important;
    }
    .link {
        font-weight: bold !important;
        cursor: pointer;
    }
}
.transport-component {
    box-sizing: border-box;
    background: white;

    @include nsb-font-container;
    font-size: 14px;
    line-height: 21px;

    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: repeat(3, auto);
    grid-column-gap: 0px;
    grid-row-gap: 0px;

    .number-wrapper {
        grid-area: 1 / 1 / 2 / 2;
    }

    .date-wrapper {
        grid-area: 1 / 2 / 2 / 3;
    }

    .data-row {
        grid-area: 2 / 1 / 3 / 3;
    }

    .bottom-row {
        grid-area: 3 / 1 / 4 / 3;
    }

    .label {
        @include nsb-select-placeholder;
        font-size: 12px;
        line-height: 18px;
    }

    .button {
        @include nsb-font-button;
        font-size: 14px !important;
    }

    .sub-price {
        @include nsb-select-placeholder;
        font-size: 10px;
        line-height: 15px;
    }

    .donor-id-data {
        @include nsb-font-container;
        font-size: 12px;
        line-height: 21px;
        border-radius: 12px;
        background-color: $ds-primary-9;
        display: inline-block;
    }

    .status {
        @include nsb-font-container;
        color: $ds-primary;
        border-radius: 12px;
        font-size: 12px;
        line-height: 18px;
        display: inline-block;

        &.CANCELED {
            background: $sd-error-2;
        }
        &.CANCELED_BY_USER {
            background: $sd-error-2; 
        }
        &.CREATED {
            background: $ds-primary-8;
        }
        &.PAYMENT_IN_PROGRESS {
            background: $ds-primary-8;
        }
        &.PAID {
            background: $ds-primary-8;
        }
        &.PLANNED {
            background: $ds-primary-8;
        }
        &.SEND {
            background: $ds-primary-8;
        }
        &.ERROR {
            background: $sd-error-2;
        }
        &.FINISHED {
            background: $ds-success-2;
        }
        &.PROCESSED {
            background: $ds-secondary-4;
        }
    }

    .status-wrapper {
        min-width: 148px;
    }

    .package-name-wrapper {
        min-width: 185px;
    }

    .timer {
        position: relative;
        cursor: pointer;
        width: 100%;
    }
    .no-refund {
        line-height: normal;
        color: $ds-primary-100;
        font-size: 12px !important;
        text-decoration: line-through;
    }
    .price-with-refund {
        line-height: normal;
        color: $ds-primary;
        font-size: 16px !important;
    }
    .mz-package-label {
        @include nsb-font-container;
        line-height: normal;
        border-radius: 100px;
        background: #F0FFF3;
        color: #0DA531;
        width: max-content;
        &.f-right {
        float: right;
        }
    }
}

.input-label {
    @include nsb-font-container;
    font-size: 14px;
    line-height: 17px;
    cursor: pointer;
    color: $ds-primary;
}

/* The container */
.container {
    display: block;
    position: relative;
    padding-left: 24px;
    cursor: pointer;
    user-select: none;
    @include nsb-font-button-regular;
    font-size: 16px !important;
    line-height: 24px;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  box-sizing: border-box;
  border: 1px solid #869AB7;
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  width: 16px;
  background-color: white;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: $nsb-disabled;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: $ds-primary;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 5px;
  top: 1px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 1px 1px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

.transport-steps-wraper {
    border-radius: 8px;
    background: $ds-background-30;
    .pause {
        height: 1px;
        background: $ds-secondary;
        max-width: 25px;
        width: 100%;
    }

    .transport-step {
        &:first-of-type {
            padding-left: 0px !important;
        }
        .step-no {
            display: flex;
            width: 24px;
            height: 24px;
            padding: 8px;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            @include nsb-font-label;
            font-size: 12px;
            line-height: 18px;
            color: $ds-primary;
            background: $ds-section-medium;
            border-radius: 100px;
            &.current {
                background: $ds-primary;
                color: $ds-white;
            }
            &.passed {
                background: $ds-icon-success;
                color: $ds-white;
            }
        }
        .step-desc {
            width: auto;
            font-size: 12px;
            line-height: 18px;
            .label {
                font-size: 12px !important;
            }
            .step-date {
                color: $ds-primary-100;
            }
        }
    }
}
</style>